import { createSignal, createEffect, createMemo, onCleanup, For, Show, untrack } from 'solid-js';
import { createStore, reconcile } from 'solid-js/store';
import { A, useNavigate } from '@solidjs/router';
import Text from '../components/Text';
import MinecraftText from '../components/MinecraftText';
import Modal from '../components/Modal';
import SellbotNav from '../components/SellbotNav';
import SellbotReasonItems from '../components/SellbotReasonItems';
import { useBotLicense } from '../hooks/useBotLicense';
import { GrantTypes } from '../services/grantsApi';
import { useApi, isOk } from '../api';
import { useActiveBot } from '../state/activeBot';
import { useToast } from '../components/MessageBox';
import { useTranslation } from '../hooks/useTranslation';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useSellbotReason } from '../hooks/useSellbotReason';
import { subscribeBotEngineEvents } from '../services/botEngineEvents';
import { formatDateTime } from '../services/dateFormat';
import { ankaufItemsSummary } from '../services/ankaufItemsSummary';
import type { SellbotConfigDto, SellbotZoneDto, SellbotOverviewDto, SellbotTransactionDto, SellbotActivityDto, AnkaufTransactionDto, BotPluginSettingsItemDto } from '../api';
import { sellbotStatusClass } from '../services/sellbotStatus';
import styles from './Sellbot.module.css';
import { firstError } from '../api/envelope';

function autoBroadcastValues(plugins: BotPluginSettingsItemDto[]): Record<string, unknown> | undefined {
    return plugins.find(p => p.plugin === 'AutoBroadcast')?.values as Record<string, unknown> | undefined;
}

function levelClass(level: string): string {
    switch (level) {
        case 'Error': return styles.badgeDanger;
        case 'Warning': return styles.badgeWarning;
        case 'Info': return styles.badgeInfo;
        default: return styles.badgeMuted;
    }
}

export default function Sellbot() {
    const api = useApi();
    const toast = useToast();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const names = useSellbotReason();
    const { reasonText, reasonItems } = names;
    const { botId } = useActiveBot();
    const grieferUtilsGuiEnabled = useFeatureFlag('GRIEFERUTILS_GUI');
    const grieferInfoStatusEnabled = useFeatureFlag('GRIEFER_INFO_STATUS');
    const autoBroadcastFeatureEnabled = useFeatureFlag('AUTO_BROADCAST');
    const { licensed } = useBotLicense(GrantTypes.SellbotSeat);

    const fmtNum = (n: number) => new Intl.NumberFormat('de-DE').format(n);
    const fmtMoney = (n: number) => `${fmtNum(n)}$`;

    const [configStore, setConfigStore] = createStore<{ config: SellbotConfigDto | null }>({ config: null });
    const config = () => configStore.config;
    const setConfig = (v: SellbotConfigDto | null) => setConfigStore('config', v ? reconcile(v, { key: 'id' }) : null);
    const [overviewStore, setOverviewStore] = createStore<{ overview: SellbotOverviewDto | null }>({ overview: null });
    const overview = () => overviewStore.overview;
    const setOverview = (v: SellbotOverviewDto | null) => setOverviewStore('overview', v ? reconcile(v, { key: 'id' }) : null);
    const [loading, setLoading] = createSignal(true);
    const [busy, setBusy] = createSignal(false);
    const [busyActive, setBusyActive] = createSignal(false);
    const [pendingShop, setPendingShop] = createSignal<{ target: boolean; sinceActivityId: string | null } | null>(null);
    const [busyAnkauf, setBusyAnkauf] = createSignal(false);
    const [busyQuote, setBusyQuote] = createSignal(false);
    const [busyPriceQuery, setBusyPriceQuery] = createSignal(false);
    const [busyPublishGu, setBusyPublishGu] = createSignal(false);
    const [busyPublishGi, setBusyPublishGi] = createSignal(false);
    const [autoBroadcastEnabled, setAutoBroadcastEnabled] = createSignal(false);
    const [busyAutoBroadcast, setBusyAutoBroadcast] = createSignal(false);
    const [detailAct, setDetailAct] = createSignal<SellbotActivityDto | null>(null);

    type FeedRow =
        | { kind: 'sale'; at: number; sale: SellbotTransactionDto }
        | { kind: 'ankauf'; at: number; ankauf: AnkaufTransactionDto };

    const feedRows = createMemo<FeedRow[]>(() => {
        const rows: FeedRow[] = [];
        for (const s of overview()?.recentTransactions ?? []) rows.push({ kind: 'sale', at: Date.parse(s.createdAt), sale: s });
        for (const a of overview()?.recentAnkaufTransactions ?? []) rows.push({ kind: 'ankauf', at: Date.parse(a.createdAt), ankauf: a });
        return rows.sort((x, y) => y.at - x.at);
    });

    const linkedTxn = (a: SellbotActivityDto) => {
        if (!a.transactionId) return null;
        const sale = (overview()?.recentTransactions ?? []).find(x => x.id === a.transactionId);
        if (sale) return { kind: 'sale' as const, sale };
        const ankauf = (overview()?.recentAnkaufTransactions ?? []).find(x => x.id === a.transactionId);
        if (ankauf) return { kind: 'ankauf' as const, ankauf };
        return null;
    };
    const closeDetail = () => setDetailAct(null);
    const openTransactionDetail = (id: string) => navigate(`/sellbot/transactions/${id}`);

    // Opening and closing the shop only queue a bot task: the bot still has to walk home, clear its
    // inventory and scan the chamber, so the button stays in a waiting state until the config flips or
    // the task writes its rejection into the activity log.
    const shopChangeTimeoutMs = 180_000;
    let pendingShopTimer: number | undefined;

    const clearPendingShop = () => {
        if (pendingShopTimer !== undefined) window.clearTimeout(pendingShopTimer);
        pendingShopTimer = undefined;
        setPendingShop(null);
    };
    onCleanup(() => { if (pendingShopTimer !== undefined) window.clearTimeout(pendingShopTimer); });

    const startPendingShop = (target: boolean) => {
        clearPendingShop();
        setPendingShop({ target, sinceActivityId: (overview()?.recentActivity ?? [])[0]?.id ?? null });
        pendingShopTimer = window.setTimeout(() => {
            clearPendingShop();
            toast?.warning(t(target ? 'SELLBOT_SHOP_OPEN_SLOW' : 'SELLBOT_SHOP_CLOSE_SLOW'));
            void loadAll();
        }, shopChangeTimeoutMs);
    };

    const activityAfterClick = (sinceActivityId: string | null): SellbotActivityDto[] => {
        const newestFirst = overview()?.recentActivity ?? [];
        const anchor = sinceActivityId ? newestFirst.findIndex(a => a.id === sinceActivityId) : -1;
        return anchor < 0 ? newestFirst : newestFirst.slice(0, anchor);
    };

    const resolvePendingShop = () => {
        const pending = pendingShop();
        if (!pending) return;
        if (config()?.isActive === pending.target) { clearPendingShop(); return; }
        const rejection = activityAfterClick(pending.sinceActivityId).find(a => a.type === 'enable_rejected');
        if (!rejection) return;
        clearPendingShop();
        toast?.error(reasonText(rejection.reasonCode, rejection.reasonArgsJson, rejection.message));
    };

    const loadAll = async () => {
        const id = botId();
        if (!id) { setConfig(null); setOverview(null); setLoading(false); return; }
        const [cfg, ov, pluginSettings] = await Promise.all([
            api.sellbot.getConfig(id),
            api.sellbot.getOverview(id),
            autoBroadcastFeatureEnabled() ? api.botPlugin.getPluginSettings(id) : Promise.resolve(null),
        ]);
        const cfgVal = isOk(cfg.data) ? cfg.data.value : null;
        setConfig(cfgVal);
        if (isOk(ov.data)) setOverview(ov.data.value);
        else toast?.error(firstError(ov.data) ?? t('SELLBOT_TOAST_FAILED'));
        if (pluginSettings && isOk(pluginSettings.data))
            setAutoBroadcastEnabled(autoBroadcastValues(pluginSettings.data.value.plugins)?.IsEnabled === true);
        setLoading(false);
        resolvePendingShop();
    };

    createEffect(() => {
        const id = botId();
        setLoading(true);
        untrack(() => { void loadAll(); });
        if (!id) return;
        const unsub = subscribeBotEngineEvents(id, (evt) => {
            if (evt.type !== 'plugin_event' || evt.payload?.source !== 'sellbot') return;
            void loadAll();
        });
        onCleanup(unsub);

        // Position/online are computed live server-side but only arrive via sale events; poll the
        // lightweight overview so "Bot auf Position" stays current as the bot moves.
        const poll = window.setInterval(() => {
            if (document.visibilityState !== 'visible') return;
            // A queued open/close changes the config too, and its event can be missed, so a pending
            // change re-reads everything instead of the overview alone.
            if (pendingShop()) { void loadAll(); return; }
            void (async () => {
                const ov = await api.sellbot.getOverview(id);
                if (isOk(ov.data)) setOverview(ov.data.value);
            })();
        }, 3000);
        onCleanup(() => window.clearInterval(poll));
    });

    const toggleActive = async () => {
        const id = botId();
        const cfg = config();
        if (!id || !cfg || pendingShop()) return;
        const target = !cfg.isActive;
        setBusyActive(true);
        startPendingShop(target);
        const res = await api.sellbot.setActive(id, { isEnabled: target });
        setBusyActive(false);
        if (!isOk(res.data)) {
            clearPendingShop();
            toast?.error(firstError(res.data) ?? t('SELLBOT_TOAST_FAILED'));
            return;
        }
        toast?.info(t(target ? 'SELLBOT_SHOP_OPENING_TOAST' : 'SELLBOT_SHOP_CLOSING_TOAST'));
        await loadAll();
    };

    const toggleSelling = async () => {
        const id = botId();
        const cfg = config();
        if (!id || !cfg) return;
        setBusy(true);
        const res = await api.sellbot.setEnabled(id, { isEnabled: !cfg.isEnabled });
        if (isOk(res.data)) await loadAll();
        else toast?.error(firstError(res.data) ?? t('SELLBOT_TOAST_FAILED'));
        setBusy(false);
    };

    const toggleAnkauf = async () => {
        const id = botId();
        const cfg = config();
        if (!id || !cfg) return;
        setBusyAnkauf(true);
        const res = await api.sellbot.setAnkaufEnabled(id, { isEnabled: !cfg.ankaufEnabled });
        if (isOk(res.data)) await loadAll();
        else toast?.error(firstError(res.data) ?? t('SELLBOT_TOAST_FAILED'));
        setBusyAnkauf(false);
    };

    const toggleQuote = async () => {
        const id = botId();
        const cfg = config();
        if (!id || !cfg) return;
        setBusyQuote(true);
        const res = await api.sellbot.setQuoteEnabled(id, { isEnabled: !cfg.quoteEnabled });
        if (isOk(res.data)) await loadAll();
        else toast?.error(firstError(res.data) ?? t('SELLBOT_TOAST_FAILED'));
        setBusyQuote(false);
    };

    const togglePriceQuery = async () => {
        const id = botId();
        const cfg = config();
        if (!id || !cfg) return;
        setBusyPriceQuery(true);
        const res = await api.sellbot.setPriceQueryEnabled(id, { isEnabled: !cfg.priceQueryEnabled });
        if (isOk(res.data)) await loadAll();
        else toast?.error(firstError(res.data) ?? t('SELLBOT_TOAST_FAILED'));
        setBusyPriceQuery(false);
    };

    const togglePublishGu = async () => {
        const id = botId();
        const cfg = config();
        if (!id || !cfg) return;
        setBusyPublishGu(true);
        const res = await api.sellbot.setPublishToGrieferUtils(id, { isEnabled: !cfg.publishToGrieferUtils });
        if (isOk(res.data)) await loadAll();
        else toast?.error(firstError(res.data) ?? t('SELLBOT_TOAST_FAILED'));
        setBusyPublishGu(false);
    };

    const togglePublishGi = async () => {
        const id = botId();
        const cfg = config();
        if (!id || !cfg) return;
        setBusyPublishGi(true);
        const res = await api.sellbot.setPublishToGrieferInfo(id, { isEnabled: !cfg.publishToGrieferInfo });
        if (isOk(res.data)) await loadAll();
        else toast?.error(firstError(res.data) ?? t('SELLBOT_TOAST_FAILED'));
        setBusyPublishGi(false);
    };

    const toggleAutoBroadcast = async () => {
        const id = botId();
        if (!id) return;
        setBusyAutoBroadcast(true);
        const settingsRes = await api.botPlugin.getPluginSettings(id);
        if (!isOk(settingsRes.data)) {
            toast?.error(firstError(settingsRes.data) ?? t('SELLBOT_TOAST_FAILED'));
            setBusyAutoBroadcast(false);
            return;
        }
        const nextEnabled = !autoBroadcastEnabled();
        const values = { ...autoBroadcastValues(settingsRes.data.value.plugins), IsEnabled: nextEnabled };
        const res = await api.botPlugin.savePluginSettings(id, 'AutoBroadcast', { settings: values as never });
        if (isOk(res.data)) setAutoBroadcastEnabled(nextEnabled);
        else toast?.error(Object.values(res.data?.errors ?? {})[0]?.[0] ?? t('SELLBOT_TOAST_FAILED'));
        setBusyAutoBroadcast(false);
    };

    // A zone carries the same setup columns as the flat config, so Single mode checks the config as its one zone.
    const everyZone = (check: (zone: SellbotZoneDto | SellbotConfigDto) => boolean): boolean => {
        const c = config();
        if (!c) return false;
        const zones = c.zoneMode === 'Multi' ? c.zones : [c];
        return zones.length > 0 && zones.every(check);
    };

    const isConfigured = (): boolean => {
        const c = config();
        if (!c) return false;
        const homeSet = c.homeName.trim() !== '';
        const areaSet = everyZone(zone => zone.sellAreaMinX !== zone.sellAreaMaxX || zone.sellAreaMinY !== zone.sellAreaMaxY || zone.sellAreaMinZ !== zone.sellAreaMaxZ);
        const chamberSet = (c.chamberChestCount ?? 0) > 0;
        return homeSet && areaSet && chamberSet;
    };

    const ankaufConfigured = (): boolean => everyZone(zone => {
        const lampSet = zone.lampX !== 0 || zone.lampY !== 0 || zone.lampZ !== 0;
        const chestSet = zone.ankaufChestX !== 0 || zone.ankaufChestY !== 0 || zone.ankaufChestZ !== 0;
        return lampSet && chestSet;
    });

    const quoteConfigured = (): boolean =>
        everyZone(zone => zone.quoteLampX !== 0 || zone.quoteLampY !== 0 || zone.quoteLampZ !== 0);

    // The sell area becomes the zone GrieferUtils uses to decide where the shop GUI opens, so an
    // all-zero box keeps the bot off the published list no matter what the toggle says.
    const sellAreaConfigured = (): boolean => everyZone(zone =>
        zone.sellAreaMinX !== 0 || zone.sellAreaMinY !== 0 || zone.sellAreaMinZ !== 0
        || zone.sellAreaMaxX !== 0 || zone.sellAreaMaxY !== 0 || zone.sellAreaMaxZ !== 0);

    const botOnline = createMemo(() => !!overview()?.online);
    const botOnPosition = createMemo(() => !!overview()?.atHome);
    const ready = createMemo(() => botOnline() && botOnPosition() && isConfigured());
    const blockedByReadiness = () => !!config()?.isActive && !ready();
    const notReadyReasonKey = (): string =>
        !botOnline() ? 'SELLBOT_NOT_READY_OFFLINE'
            : !botOnPosition() ? 'SELLBOT_NOT_READY_OFF_POSITION'
                : 'SELLBOT_NOT_READY_NOT_CONFIGURED';
    const sellingShownOn = () => config()!.isActive ? (ready() && config()!.isEnabled) : config()!.isEnabled;
    const ankaufShownOn = () => config()!.isActive ? (ready() && config()!.ankaufEnabled) : config()!.ankaufEnabled;
    const quoteShownOn = () => config()!.isActive ? (ready() && config()!.quoteEnabled) : config()!.quoteEnabled;
    const priceQueryShownOn = () => config()!.isActive ? (ready() && config()!.priceQueryEnabled) : config()!.priceQueryEnabled;
    const publishGuShownOn = () => config()!.publishToGrieferUtils && sellAreaConfigured();
    const guDomainAssigned = () => config()!.botshopDomainAssigned;
    // Standing at home is not part of it: the update says the shop is open and the bot is connected,
    // which is all griefer.info lists.
    const publishGiShownOn = () => config()!.isActive
        ? (botOnline() && config()!.publishToGrieferInfo)
        : config()!.publishToGrieferInfo;

    const statusRow = (ok: boolean, icon: string, label: string, okLabel: string, badLabel: string) => (
        <div class={styles.statusItem}>
            <span class={`${styles.statusDot} ${ok ? styles.statusDotOk : styles.statusDotOff}`}><i class={`fa-solid ${icon}`} /></span>
            <span class={styles.statusItemLabel}><Text val={label} /></span>
            <span class={`${styles.badge} ${ok ? styles.badgeSuccess : styles.badgeMuted}`}>{t(ok ? okLabel : badLabel)}</span>
        </div>
    );

    return (
        <div class={styles.container}>
            <SellbotNav />
            <div class={styles.headerRow}>
                <div class="v" style={{ gap: '4px' }}>
                    <h1><Text val="SELLBOT_TITLE" /></h1>
                    <span class={styles.heroSubtitle}><Text val="SELLBOT_MANAGE_SUBTITLE" /></span>
                </div>
            </div>

            <Show when={botId()} fallback={
                <div class={styles.empty}><i class="fa-solid fa-robot" style={{ 'font-size': '3rem', opacity: 0.5 }} /><p><Text val="SELLBOT_NO_BOT" /></p></div>
            }>
                <Show when={!loading()} fallback={<div class={styles.skeletonGrid}><For each={[1,2,3,4,5,6]}>{() => <div class={styles.skeletonCard} />}</For></div>}>
                    <Show when={config()} fallback={
                        <div class={styles.empty}>
                            <i class="fa-solid fa-store-slash" style={{ 'font-size': '3rem', opacity: 0.5 }} />
                            <p><Text val="SELLBOT_NOT_CONFIGURED" /></p>
                            <A href="/sellbot/config" class="btn btn-primary"><Text val="SELLBOT_SETTINGS_NAV" /></A>
                        </div>
                    }>
                        <div class={`fcard ${styles.toggleBar} ${config()!.isActive ? styles.toggleBarOn : styles.toggleBarOff}`}>
                            <div class={styles.toggleStateBlock}>
                                <div class={`${styles.toggleIcon} ${config()!.isActive ? styles.toggleIconOn : styles.toggleIconOff}`}>
                                    <i class={`fa-solid ${config()!.isActive ? 'fa-store' : 'fa-store-slash'}`} />
                                </div>
                                <div class="v" style={{ gap: '2px' }}>
                                    <span class={styles.toggleTitle}>
                                        {pendingShop()
                                            ? t(pendingShop()!.target ? 'SELLBOT_SHOP_OPENING' : 'SELLBOT_SHOP_CLOSING')
                                            : t(config()!.isActive ? 'SELLBOT_SHOP_OPEN' : 'SELLBOT_SHOP_CLOSED')}
                                    </span>
                                    <span class={styles.toggleHint}>
                                        {pendingShop()
                                            ? t(pendingShop()!.target ? 'SELLBOT_SHOP_OPENING_HINT' : 'SELLBOT_SHOP_CLOSING_HINT')
                                            : t(!config()!.isActive ? 'SELLBOT_SHOP_CLOSED_HINT' : ready() ? 'SELLBOT_SHOP_OPEN_HINT' : 'SELLBOT_SHOP_OPEN_NOT_READY_HINT')}
                                        <span style={{ 'margin-left': '8px' }} class={`${styles.badge} ${overview()?.online ? styles.badgeSuccess : styles.badgeMuted}`}>
                                            {t(overview()?.online ? 'SELLBOT_ONLINE' : 'SELLBOT_OFFLINE')}
                                        </span>
                                    </span>
                                </div>
                            </div>
                            <button class={`btn ${config()!.isActive ? 'btn-danger' : 'btn-success'}`} disabled={busyActive() || !!pendingShop() || (!config()!.isActive && !licensed())} onClick={() => void toggleActive()}>
                                <Show when={pendingShop()} fallback={<i class={`fa-solid ${config()!.isActive ? 'fa-store-slash' : 'fa-store'}`} />}>
                                    <i class="fa-solid fa-spinner fa-spin" />
                                </Show>
                                <span style={{ 'margin-left': '6px' }}>
                                    {pendingShop()
                                        ? t(pendingShop()!.target ? 'SELLBOT_SHOP_OPENING' : 'SELLBOT_SHOP_CLOSING')
                                        : t(config()!.isActive ? 'SELLBOT_SHOP_CLOSE' : 'SELLBOT_SHOP_OPEN_BTN')}
                                </span>
                            </button>
                        </div>

                        <div class={styles.subToggles} classList={{ [styles.subTogglesDimmed]: !config()!.isActive }}>
                            <Show when={!config()!.isActive}>
                                <span class={styles.subToggleNote}><i class="fa-solid fa-circle-info" /> <Text val="SELLBOT_SUBTOGGLE_NOTE" /></span>
                            </Show>

                            <div class={`${styles.subToggle} ${sellingShownOn() ? styles.subToggleOn : styles.subToggleOff}`}>
                                <div class={styles.subToggleState}>
                                    <i class={`fa-solid ${sellingShownOn() ? 'fa-circle-play' : 'fa-circle-pause'} ${styles.subToggleIcon} ${sellingShownOn() ? styles.subToggleIconOn : styles.subToggleIconOff}`} />
                                    <div class="v" style={{ gap: '2px' }}>
                                        <span class={styles.subToggleTitle}>{t(sellingShownOn() ? 'SELLBOT_SELLING_ON' : 'SELLBOT_SELLING_PAUSED')}</span>
                                        <span class={styles.subToggleHint}>{t(blockedByReadiness() ? notReadyReasonKey() : config()!.isEnabled ? 'SELLBOT_SELLING_ON_HINT' : 'SELLBOT_SELLING_PAUSED_HINT')}</span>
                                    </div>
                                </div>
                                <button class={`btn btn-sm ${config()!.isEnabled ? 'btn-danger' : 'btn-success'}`} disabled={busy()} onClick={() => void toggleSelling()}>
                                    <i class={`fa-solid ${config()!.isEnabled ? 'fa-pause' : 'fa-play'}`} />
                                    <span style={{ 'margin-left': '6px' }}>{t(config()!.isEnabled ? 'SELLBOT_PAUSE_SELLING' : 'SELLBOT_START_SELLING')}</span>
                                </button>
                            </div>

                            <div class={`${styles.subToggle} ${ankaufShownOn() ? styles.subToggleOn : styles.subToggleOff}`}>
                                <div class={styles.subToggleState}>
                                    <i class={`fa-solid ${ankaufShownOn() ? 'fa-cart-shopping' : 'fa-cart-arrow-down'} ${styles.subToggleIcon} ${ankaufShownOn() ? styles.subToggleIconOn : styles.subToggleIconOff}`} />
                                    <div class="v" style={{ gap: '2px' }}>
                                        <span class={styles.subToggleTitle}>{t(ankaufShownOn() ? 'SELLBOT_ANKAUF_ON' : 'SELLBOT_ANKAUF_PAUSED')}</span>
                                        <Show when={ankaufConfigured()} fallback={
                                            <A href="/sellbot/config" class={styles.subToggleHint}><i class="fa-solid fa-circle-info" /> <Text val="SELLBOT_ANKAUF_NEEDS_CONFIG" /></A>
                                        }>
                                            <span class={styles.subToggleHint}>{t(blockedByReadiness() ? notReadyReasonKey() : config()!.ankaufEnabled ? 'SELLBOT_ANKAUF_ON_HINT' : 'SELLBOT_ANKAUF_PAUSED_HINT')}</span>
                                        </Show>
                                    </div>
                                </div>
                                <button class={`btn btn-sm ${config()!.ankaufEnabled ? 'btn-danger' : 'btn-success'}`} disabled={busyAnkauf() || (!config()!.ankaufEnabled && !ankaufConfigured())} onClick={() => void toggleAnkauf()}>
                                    <i class={`fa-solid ${config()!.ankaufEnabled ? 'fa-pause' : 'fa-play'}`} />
                                    <span style={{ 'margin-left': '6px' }}>{t(config()!.ankaufEnabled ? 'SELLBOT_ANKAUF_PAUSE' : 'SELLBOT_ANKAUF_START')}</span>
                                </button>
                            </div>

                            <div class={`${styles.subToggle} ${quoteShownOn() ? styles.subToggleOn : styles.subToggleOff}`}>
                                <div class={styles.subToggleState}>
                                    <i class={`fa-solid ${quoteShownOn() ? 'fa-file-invoice-dollar' : 'fa-calculator'} ${styles.subToggleIcon} ${quoteShownOn() ? styles.subToggleIconOn : styles.subToggleIconOff}`} />
                                    <div class="v" style={{ gap: '2px' }}>
                                        <span class={styles.subToggleTitle}>{t(quoteShownOn() ? 'SELLBOT_QUOTE_ON' : 'SELLBOT_QUOTE_PAUSED')}</span>
                                        <Show when={quoteConfigured()} fallback={
                                            <A href="/sellbot/config" class={styles.subToggleHint}><i class="fa-solid fa-circle-info" /> <Text val="SELLBOT_QUOTE_NEEDS_CONFIG" /></A>
                                        }>
                                            <span class={styles.subToggleHint}>{t(blockedByReadiness() ? notReadyReasonKey() : config()!.quoteEnabled ? 'SELLBOT_QUOTE_ON_HINT' : 'SELLBOT_QUOTE_PAUSED_HINT')}</span>
                                        </Show>
                                    </div>
                                </div>
                                <button class={`btn btn-sm ${config()!.quoteEnabled ? 'btn-danger' : 'btn-success'}`} disabled={busyQuote() || (!config()!.quoteEnabled && !quoteConfigured())} onClick={() => void toggleQuote()}>
                                    <i class={`fa-solid ${config()!.quoteEnabled ? 'fa-pause' : 'fa-play'}`} />
                                    <span style={{ 'margin-left': '6px' }}>{t(config()!.quoteEnabled ? 'SELLBOT_QUOTE_PAUSE' : 'SELLBOT_QUOTE_START')}</span>
                                </button>
                            </div>

                            <div class={`${styles.subToggle} ${priceQueryShownOn() ? styles.subToggleOn : styles.subToggleOff}`}>
                                <div class={styles.subToggleState}>
                                    <i class={`fa-solid ${priceQueryShownOn() ? 'fa-comments-dollar' : 'fa-comment-dollar'} ${styles.subToggleIcon} ${priceQueryShownOn() ? styles.subToggleIconOn : styles.subToggleIconOff}`} />
                                    <div class="v" style={{ gap: '2px' }}>
                                        <span class={styles.subToggleTitle}>{t(priceQueryShownOn() ? 'SELLBOT_PRICEQUERY_ON' : 'SELLBOT_PRICEQUERY_PAUSED')}</span>
                                        <span class={styles.subToggleHint}>{t(blockedByReadiness() ? notReadyReasonKey() : config()!.priceQueryEnabled ? 'SELLBOT_PRICEQUERY_ON_HINT' : 'SELLBOT_PRICEQUERY_PAUSED_HINT')}</span>
                                    </div>
                                </div>
                                <button class={`btn btn-sm ${config()!.priceQueryEnabled ? 'btn-danger' : 'btn-success'}`} disabled={busyPriceQuery()} onClick={() => void togglePriceQuery()}>
                                    <i class={`fa-solid ${config()!.priceQueryEnabled ? 'fa-pause' : 'fa-play'}`} />
                                    <span style={{ 'margin-left': '6px' }}>{t(config()!.priceQueryEnabled ? 'SELLBOT_PRICEQUERY_PAUSE' : 'SELLBOT_PRICEQUERY_START')}</span>
                                </button>
                            </div>

                            <Show when={grieferUtilsGuiEnabled()}>
                                <div class={`${styles.subToggle} ${publishGuShownOn() ? styles.subToggleOn : styles.subToggleOff}`}>
                                    <div class={styles.subToggleState}>
                                        <i class={`fa-solid ${publishGuShownOn() ? 'fa-share-nodes' : 'fa-eye-slash'} ${styles.subToggleIcon} ${publishGuShownOn() ? styles.subToggleIconOn : styles.subToggleIconOff}`} />
                                        <div class="v" style={{ gap: '2px' }}>
                                            <span class={styles.subToggleTitle}>{t(publishGuShownOn() ? 'SELLBOT_GU_ON' : 'SELLBOT_GU_PAUSED')}</span>
                                            <Show when={sellAreaConfigured()} fallback={
                                                <A href="/sellbot/config" class={styles.subToggleHint}><i class="fa-solid fa-circle-info" /> <Text val="SELLBOT_GU_NEEDS_CONFIG" /></A>
                                            }>
                                                <Show when={guDomainAssigned()} fallback={
                                                    <span class={styles.subToggleHint}><i class="fa-solid fa-circle-info" /> <Text val="SELLBOT_GU_NO_DOMAIN_HINT" /></span>
                                                }>
                                                    <span class={styles.subToggleHint}>{t(config()!.publishToGrieferUtils ? 'SELLBOT_GU_ON_HINT' : 'SELLBOT_GU_PAUSED_HINT')}</span>
                                                </Show>
                                            </Show>
                                        </div>
                                    </div>
                                    <button class={`btn btn-sm ${config()!.publishToGrieferUtils ? 'btn-danger' : 'btn-success'}`} disabled={busyPublishGu() || (!config()!.publishToGrieferUtils && (!sellAreaConfigured() || !guDomainAssigned()))} onClick={() => void togglePublishGu()}>
                                        <i class={`fa-solid ${config()!.publishToGrieferUtils ? 'fa-pause' : 'fa-play'}`} />
                                        <span style={{ 'margin-left': '6px' }}>{t(config()!.publishToGrieferUtils ? 'SELLBOT_GU_PAUSE' : 'SELLBOT_GU_START')}</span>
                                    </button>
                                </div>
                            </Show>

                            <Show when={grieferInfoStatusEnabled()}>
                                <div class={`${styles.subToggle} ${publishGiShownOn() ? styles.subToggleOn : styles.subToggleOff}`}>
                                    <div class={styles.subToggleState}>
                                        <i class={`fa-solid ${publishGiShownOn() ? 'fa-tower-broadcast' : 'fa-tower-observation'} ${styles.subToggleIcon} ${publishGiShownOn() ? styles.subToggleIconOn : styles.subToggleIconOff}`} />
                                        <div class="v" style={{ gap: '2px' }}>
                                            <span class={styles.subToggleTitle}>{t(publishGiShownOn() ? 'SELLBOT_GI_ON' : 'SELLBOT_GI_PAUSED')}</span>
                                            <span class={styles.subToggleHint}>{t(config()!.isActive && !botOnline() ? 'SELLBOT_NOT_READY_OFFLINE' : config()!.publishToGrieferInfo ? 'SELLBOT_GI_ON_HINT' : 'SELLBOT_GI_PAUSED_HINT')}</span>
                                        </div>
                                    </div>
                                    <button class={`btn btn-sm ${config()!.publishToGrieferInfo ? 'btn-danger' : 'btn-success'}`} disabled={busyPublishGi()} onClick={() => void togglePublishGi()}>
                                        <i class={`fa-solid ${config()!.publishToGrieferInfo ? 'fa-pause' : 'fa-play'}`} />
                                        <span style={{ 'margin-left': '6px' }}>{t(config()!.publishToGrieferInfo ? 'SELLBOT_GI_PAUSE' : 'SELLBOT_GI_START')}</span>
                                    </button>
                                </div>
                            </Show>

                            <Show when={autoBroadcastFeatureEnabled()}>
                                <div class={`${styles.subToggle} ${autoBroadcastEnabled() ? styles.subToggleOn : styles.subToggleOff}`}>
                                    <div class={styles.subToggleState}>
                                        <i class={`fa-solid ${autoBroadcastEnabled() ? 'fa-bullhorn' : 'fa-volume-xmark'} ${styles.subToggleIcon} ${autoBroadcastEnabled() ? styles.subToggleIconOn : styles.subToggleIconOff}`} />
                                        <div class="v" style={{ gap: '2px' }}>
                                            <span class={styles.subToggleTitle}>{t(autoBroadcastEnabled() ? 'SELLBOT_AUTOBROADCAST_ON' : 'SELLBOT_AUTOBROADCAST_PAUSED')}</span>
                                            <span class={styles.subToggleHint}>{t(autoBroadcastEnabled() ? 'SELLBOT_AUTOBROADCAST_ON_HINT' : 'SELLBOT_AUTOBROADCAST_PAUSED_HINT')}</span>
                                        </div>
                                    </div>
                                    <button class={`btn btn-sm ${autoBroadcastEnabled() ? 'btn-danger' : 'btn-success'}`} disabled={busyAutoBroadcast()} onClick={() => void toggleAutoBroadcast()}>
                                        <i class={`fa-solid ${autoBroadcastEnabled() ? 'fa-pause' : 'fa-play'}`} />
                                        <span style={{ 'margin-left': '6px' }}>{t(autoBroadcastEnabled() ? 'SELLBOT_AUTOBROADCAST_PAUSE' : 'SELLBOT_AUTOBROADCAST_START')}</span>
                                    </button>
                                </div>
                            </Show>
                        </div>

                        <div class="fcard v" style={{ gap: 'var(--spacing-md)' }}>
                            <div class={styles.sectionHeader}>
                                <h2><Text val="SELLBOT_STATUS" /></h2>
                                <span class={styles.helper}><Text val="SELLBOT_STATUS_HELP" /></span>
                            </div>
                            <div class={styles.statusList}>
                                {statusRow(!!overview()?.online, 'fa-plug', 'SELLBOT_STATUS_ONLINE', 'SELLBOT_ONLINE', 'SELLBOT_OFFLINE')}
                                {statusRow(!!overview()?.atHome, 'fa-location-dot', 'SELLBOT_STATUS_AT_HOME', 'SELLBOT_STATUS_YES', 'SELLBOT_STATUS_NO')}
                                {statusRow(isConfigured(), 'fa-sliders', 'SELLBOT_STATUS_CONFIGURED', 'SELLBOT_STATUS_YES', 'SELLBOT_STATUS_NO')}
                            </div>
                        </div>

                        <div class={styles.statsGrid}>
                            <div class="fcard"><span class={styles.statLabel}><Text val="SELLBOT_REVENUE" /></span><span class={styles.statValue}>{fmtMoney(overview()?.totalRevenue ?? 0)}</span></div>
                            <div class="fcard"><span class={styles.statLabel}><Text val="SELLBOT_SALES" /></span><span class={styles.statValue}>{fmtNum(overview()?.sales ?? 0)}</span></div>
                            <div class="fcard"><span class={styles.statLabel}><Text val="SELLBOT_REFUNDS" /></span><span class={styles.statValue}>{fmtNum(overview()?.refunds ?? 0)}</span></div>
                            <div class="fcard"><span class={styles.statLabel}><Text val="SELLBOT_OFFERS" /></span><span class={styles.statValue}>{fmtNum(overview()?.offerCount ?? 0)}</span></div>
                            <div class="fcard"><span class={styles.statLabel}><Text val="SELLBOT_OUT_OF_STOCK" /></span><span class={styles.statValue}>{fmtNum(overview()?.outOfStockOffers ?? 0)}</span></div>
                            <div class="fcard"><span class={styles.statLabel}><Text val="SELLBOT_IGNORED" /></span><span class={styles.statValue}>{fmtNum(overview()?.ignored ?? 0)}</span></div>
                        </div>

                        <div class={styles.columns}>
                            <div class="fcard v" style={{ gap: 'var(--spacing-md)', flex: 1 }}>
                                <div class="h hcenter" style={{ 'justify-content': 'space-between' }}>
                                    <h2 class={styles.sectionTitle}><Text val="SELLBOT_RECENT_TRANSACTIONS" /></h2>
                                    <A href="/sellbot/transactions" class={styles.helper}><Text val="SELLBOT_VIEW_ALL" /> <i class="fa-solid fa-arrow-right" /></A>
                                </div>
                                <Show when={feedRows().length > 0} fallback={<div class={styles.emptySmall}><i class="fa-solid fa-receipt" style={{ 'font-size': '2rem', opacity: 0.4 }} /><p><Text val="SELLBOT_NO_TRANSACTIONS" /></p></div>}>
                                    <div class={styles.feed}>
                                        <For each={feedRows()}>{(row) => (
                                            <Show when={row.kind === 'sale'} fallback={(() => { const a = (row as { ankauf: AnkaufTransactionDto }).ankauf; const summary = ankaufItemsSummary(a.items, names); return (
                                                <div class={`${styles.txnRowMerged} ${styles.clickable}`} role="button" tabindex="0" title={t('SELLBOT_DETAIL_OPEN')} onClick={() => openTransactionDetail(a.id)}>
                                                    <span class={`${styles.txnKind} ${styles.txnKindAnkauf}`} title={t('SELLBOT_KIND_ANKAUF')}><i class="fa-solid fa-cart-shopping" /></span>
                                                    <span class={`${styles.badge} ${sellbotStatusClass(a.status, styles)}`}>{t('SELLBOT_TXN_' + a.status)}</span>
                                                    <span class={styles.txnPayer}>
                                                        <MinecraftText text={a.sellerName} />
                                                        <Show when={summary}><span class={styles.txnItem}> · {summary}</span></Show>
                                                    </span>
                                                    <span class={styles.txnAmount}>{fmtMoney(a.amountPaid)}</span>
                                                    <span class={styles.txnTime}>{formatDateTime(a.createdAt)}</span>
                                                </div>
                                            ); })()}>
                                                {(() => { const s = (row as { sale: SellbotTransactionDto }).sale; return (
                                                    <div class={`${styles.txnRowMerged} ${styles.clickable}`} role="button" tabindex="0" title={t('SELLBOT_DETAIL_OPEN')} onClick={() => openTransactionDetail(s.id)}>
                                                        <span class={`${styles.txnKind} ${styles.txnKindSale}`} title={t('SELLBOT_KIND_SALE')}><i class="fa-solid fa-hand-holding-dollar" /></span>
                                                        <span class={`${styles.badge} ${sellbotStatusClass(s.status, styles)}`}>{t('SELLBOT_TXN_' + s.status)}</span>
                                                        <span class={styles.txnPayer}><MinecraftText text={s.payerName} /></span>
                                                        <span class={styles.txnAmount}>{fmtMoney(s.amount)}</span>
                                                        <span class={styles.txnTime}>{formatDateTime(s.createdAt)}</span>
                                                    </div>
                                                ); })()}
                                            </Show>
                                        )}</For>
                                    </div>
                                </Show>
                            </div>

                            <div class="fcard v" style={{ gap: 'var(--spacing-md)', flex: 1 }}>
                                <h2 class={styles.sectionTitle}><Text val="SELLBOT_ACTIVITY" /></h2>
                                <Show when={(overview()?.recentActivity ?? []).length > 0} fallback={<div class={styles.emptySmall}><i class="fa-solid fa-stream" style={{ 'font-size': '2rem', opacity: 0.4 }} /><p><Text val="SELLBOT_NO_ACTIVITY" /></p></div>}>
                                    <div class={styles.feed}>
                                        <For each={overview()?.recentActivity ?? []}>{(a) => (
                                            <div class={`${styles.actRow} ${styles.clickable}`} role="button" tabindex="0" title={t('SELLBOT_DETAIL_OPEN')} onClick={() => setDetailAct(a)}>
                                                <span class={`${styles.badge} ${levelClass(a.level)}`}>{t('SELLBOT_LOG_' + a.level.toUpperCase())}</span>
                                                <span class={styles.actType}>{t('SELLBOT_ACT_' + a.type)}<Show when={a.playerName}> · <MinecraftText text={a.playerName!} /></Show></span>
                                                <span class={styles.txnTime}>{formatDateTime(a.createdAt)}</span>
                                                <Show when={reasonItems(a.reasonArgsJson)}>
                                                    {(items) => <div class={styles.actItems}><SellbotReasonItems items={items()} showNames={false} /></div>}
                                                </Show>
                                            </div>
                                        )}</For>
                                    </div>
                                </Show>
                            </div>
                        </div>
                    </Show>
                </Show>
            </Show>

            <Modal open={!!detailAct()} onClose={closeDetail} title={<Text val="SELLBOT_DETAIL_ACT_TITLE" />} subtitle={<Text val="SELLBOT_DETAIL_SUBTITLE" />}>
                <Show when={detailAct()}>{(a) => (
                    <div class={styles.detailGrid}>
                        <span class={styles.detailLabel}><Text val="SELLBOT_DETAIL_LEVEL" /></span>
                        <span><span class={`${styles.badge} ${levelClass(a().level)}`}>{t('SELLBOT_LOG_' + a().level.toUpperCase())}</span></span>
                        <span class={styles.detailLabel}><Text val="SELLBOT_DETAIL_TYPE" /></span>
                        <span>{t('SELLBOT_ACT_' + a().type)}</span>
                        <Show when={a().reasonCode || a().message}>
                            <span class={styles.detailLabel}><Text val="SELLBOT_DETAIL_MESSAGE" /></span>
                            <span class={styles.detailMessage}>{reasonText(a().reasonCode, a().reasonArgsJson, a().message)}</span>
                        </Show>
                        <Show when={reasonItems(a().reasonArgsJson)}>
                            {(items) => (
                                <>
                                    <span class={styles.detailLabel}><Text val="SELLBOT_DETAIL_HELD_ITEMS" /></span>
                                    <SellbotReasonItems items={items()} />
                                </>
                            )}
                        </Show>
                        <Show when={a().playerName}>
                            <span class={styles.detailLabel}><Text val="SELLBOT_DETAIL_PLAYER" /></span>
                            <span><MinecraftText text={a().playerName!} /></span>
                        </Show>
                        <span class={styles.detailLabel}><Text val="SELLBOT_DETAIL_CREATED" /></span>
                        <span>{formatDateTime(a().createdAt)}</span>
                        <Show when={linkedTxn(a())}>{(lt) => (
                            <>
                                <span class={styles.detailLabel}><Text val="SELLBOT_DETAIL_LINKED_TXN" /></span>
                                <Show when={lt().kind === 'sale'} fallback={
                                    <span class={styles.clickable} role="button" tabindex="0" title={t('SELLBOT_DETAIL_OPEN')}
                                          onClick={() => { setDetailAct(null); openTransactionDetail(lt().ankauf!.id); }}>
                                        <span class={`${styles.badge} ${sellbotStatusClass(lt().ankauf!.status, styles)}`}>{t('SELLBOT_TXN_' + lt().ankauf!.status)}</span> {reasonText(null, null, lt().ankauf!.reason)}
                                    </span>
                                }>
                                    <span class={styles.clickable} role="button" tabindex="0" title={t('SELLBOT_DETAIL_OPEN')}
                                          onClick={() => { setDetailAct(null); openTransactionDetail(lt().sale!.id); }}>
                                        <span class={`${styles.badge} ${sellbotStatusClass(lt().sale!.status, styles)}`}>{t('SELLBOT_TXN_' + lt().sale!.status)}</span> {reasonText(lt().sale!.reasonCode, lt().sale!.reasonArgsJson, lt().sale!.reason)}
                                    </span>
                                </Show>
                            </>
                        )}</Show>
                    </div>
                )}</Show>
            </Modal>
        </div>
    );
}
